import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Settings,
  Bell,
  HelpCircle,
  Grid3X3,
  Menu,
  Moon,
  Sun,
  User,
  LogOut,
  Shield,
  Users,
  Gift,
  AlertTriangle,
  RefreshCw,
  Loader2,
  MessageCircle,
  UserPlus,
  Check,
  X as XIcon,
  Mail,
  Phone,
  Code
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Badge } from '../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel
} from '../ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { cn } from '../../lib/utils';
import ReportModal from '../support/ReportModal';
import NotificationSettings from '../NotificationSettings';
import ContactFormModal from '../contact/ContactFormModal';
import { messengerApi, broadcastsApi, userNotificationsApi } from '../../api/api';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';

// Format time ago
function formatTimeAgo(date) {
  if (!date) return '';
  const now = new Date();
  const diff = Math.floor((now - new Date(date)) / 1000);
  
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function Header({ 
  onMenuToggle, 
  searchQuery, 
  onSearchChange,
  isDarkMode: isDarkModeProp,
  onToggleTheme,
  user: userProp,
  onLogout: onLogoutProp,
  isAdmin: isAdminProp,
  onCheckMail,
  refreshing,
  lastRefresh,
  onToggleMessenger,
  messengerUnreadCount
}) {
  const navigate = useNavigate();
  const { user: authUser, isAdmin: authIsAdmin, logout: authLogout } = useAuth();
  const { isDarkMode: themeIsDark, toggleTheme } = useTheme();
  
  // Use props if provided, otherwise fall back to context
  const user = userProp || authUser;
  const isAdmin = isAdminProp !== undefined ? isAdminProp : authIsAdmin;
  const onLogout = onLogoutProp || authLogout;
  const isDarkMode = isDarkModeProp !== undefined ? isDarkModeProp : themeIsDark;
  const { primaryColor } = useTheme();
  const [searchFocused, setSearchFocused] = useState(false);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [friendRequests, setFriendRequests] = useState([]);
  const [broadcasts, setBroadcasts] = useState([]);
  const [supportNotifications, setSupportNotifications] = useState([]);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [loadingBroadcasts, setLoadingBroadcasts] = useState(false);
  const [loadingSupport, setLoadingSupport] = useState(false);
  const prevFriendRequestCount = useRef(0);
  const prevBroadcastCount = useRef(0);
  const prevSupportCount = useRef(0);
  const audioRef = useRef(null);

  // Fetch friend requests
  const fetchFriendRequests = async () => {
    try {
      setLoadingRequests(true);
      const data = await messengerApi.getFriendRequests();
      const incoming = data.incoming || [];
      
      // Play notification sound if new requests arrived
      if (incoming.length > prevFriendRequestCount.current && prevFriendRequestCount.current > 0) {
        playNotificationSound();
      }
      prevFriendRequestCount.current = incoming.length;
      
      setFriendRequests(incoming);
    } catch (err) {
      console.error('Failed to fetch friend requests:', err);
    } finally {
      setLoadingRequests(false);
    }
  };

  // Play notification sound
  const playNotificationSound = () => {
    const fromStorage = localStorage.getItem('notification_sound_enabled');
    const fromCookie = document.cookie.match(/notification_sound_enabled=([^;]+)/)?.[1];
    const soundEnabled = (fromStorage || fromCookie || 'true') !== 'false';
    if (soundEnabled) {
      try {
        if (!audioRef.current) {
          audioRef.current = new Audio('/sounds/message.ogg');
        }
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(() => {});
      } catch (err) {
        console.error('Failed to play notification sound:', err);
      }
    }
  };

  // Fetch broadcasts
  const fetchBroadcasts = async () => {
    try {
      setLoadingBroadcasts(true);
      const data = await broadcastsApi.getMyBroadcasts(false);
      const unreadBroadcasts = (data || []).filter(b => !b.is_read);
      
      // Play notification sound if new broadcasts arrived
      if (unreadBroadcasts.length > prevBroadcastCount.current && prevBroadcastCount.current >= 0) {
        playNotificationSound();
      }
      prevBroadcastCount.current = unreadBroadcasts.length;
      
      setBroadcasts(data || []);
    } catch (err) {
      console.error('Failed to fetch broadcasts:', err);
    } finally {
      setLoadingBroadcasts(false);
    }
  };

  // Handle marking broadcast as read
  const handleMarkBroadcastRead = async (broadcastId) => {
    try {
      await broadcastsApi.markAsRead(broadcastId);
      setBroadcasts(prev => prev.map(b => 
        b.id === broadcastId ? { ...b, is_read: true } : b
      ));
    } catch (err) {
      console.error('Failed to mark broadcast as read:', err);
    }
  };

  // Handle marking all broadcasts as read
  const handleMarkAllBroadcastsRead = async () => {
    try {
      await broadcastsApi.markAllAsRead();
      setBroadcasts(prev => prev.map(b => ({ ...b, is_read: true })));
    } catch (err) {
      console.error('Failed to mark all broadcasts as read:', err);
    }
  };

  const handleDismissBroadcast = async (broadcastId, e) => {
    e.stopPropagation();
    try {
      await broadcastsApi.dismiss(broadcastId);
      setBroadcasts(prev => prev.filter(b => b.id !== broadcastId));
    } catch (err) {
      console.error('Failed to dismiss broadcast:', err);
    }
  };

  // Fetch support notifications
  const fetchSupportNotifications = async () => {
    try {
      setLoadingSupport(true);
      const data = await userNotificationsApi.getNotifications(false);
      const unreadSupport = (data || []).filter(n => !n.is_read);
      
      // Play notification sound if new support replies arrived
      if (unreadSupport.length > prevSupportCount.current && prevSupportCount.current >= 0) {
        playNotificationSound();
      }
      prevSupportCount.current = unreadSupport.length;
      
      setSupportNotifications(data || []);
    } catch (err) {
      console.error('Failed to fetch support notifications:', err);
    } finally {
      setLoadingSupport(false);
    }
  };

  // Handle marking support notification as read
  const handleMarkSupportRead = async (notificationId) => {
    try {
      await userNotificationsApi.markAsRead(notificationId);
      setSupportNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, is_read: true } : n
      ));
    } catch (err) {
      console.error('Failed to mark support notification as read:', err);
    }
  };

  // Handle dismissing support notification
  const handleDismissSupportNotification = async (notificationId, e) => {
    e.stopPropagation();
    try {
      await userNotificationsApi.dismiss(notificationId);
      setSupportNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (err) {
      console.error('Failed to dismiss notification:', err);
    }
  };

  // Handle friend request response
  const handleRespondToRequest = async (requestId, action) => {
    try {
      await messengerApi.respondToFriendRequest(requestId, action);
      fetchFriendRequests();
    } catch (err) {
      console.error('Failed to respond to request:', err);
    }
  };

  // Fetch friend requests, broadcasts, and support notifications on mount and periodically
  useEffect(() => {
    fetchFriendRequests();
    fetchBroadcasts();
    fetchSupportNotifications();
    const interval = setInterval(() => {
      fetchFriendRequests();
      fetchBroadcasts();
      fetchSupportNotifications();
    }, 30000); // Check every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const apps = [
    { name: 'Mail', icon: '✉️', active: true, onClick: () => navigate('/inbox') },
    { name: 'Wall', icon: '📰', active: false, onClick: () => navigate('/wall') },
    { name: 'Drive', icon: '☁️', active: false, disabled: true },
  ];

  // Theme-aware classes
  const headerBg = isDarkMode ? 'bg-[#1a1a2e]' : 'bg-white';
  const borderColor = isDarkMode ? 'border-[#2d2d44]' : 'border-gray-200';
  const textColor = isDarkMode ? 'text-gray-400' : 'text-gray-600';
  const textHover = isDarkMode ? 'hover:text-white' : 'hover:text-gray-900';
  const buttonHover = isDarkMode ? 'hover:bg-[#2d2d44]' : 'hover:bg-gray-100';
  const inputBg = isDarkMode ? 'bg-[#252540]' : 'bg-gray-100';
  const inputBorder = isDarkMode ? 'border-[#2d2d44]' : 'border-gray-200';
  const inputText = isDarkMode ? 'text-white' : 'text-gray-900';
  const popoverBg = isDarkMode ? 'bg-[#1a1a2e]' : 'bg-white';

  return (
    <header className={`h-14 ${headerBg} border-b ${borderColor} flex items-center justify-between px-4 gap-4`}>
      {/* Left Section */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={onMenuToggle}
          className={`md:hidden ${textColor} ${textHover} ${buttonHover}`}
        >
          <Menu className="w-5 h-5" />
        </Button>

        {/* Check Mail Button */}
        {onCheckMail && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onCheckMail}
                  disabled={refreshing}
                  className={cn(
                    `hidden md:flex items-center gap-2 ${textColor} ${textHover} ${buttonHover}`,
                    refreshing && "opacity-70"
                  )}
                  data-testid="check-mail-btn"
                >
                  {refreshing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                  <span className="text-sm">Check Mail</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent className={`${popoverBg} ${borderColor}`}>
                <p className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                  {lastRefresh ? `Last checked: ${formatTimeAgo(lastRefresh)}` : 'Check for new mail'}
                </p>
                <p className="text-xs text-gray-500">Auto-refreshes every 5 min</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      {/* Search Bar */}
      <div className="flex-1 max-w-2xl">
        <div className={cn(
          "relative flex items-center transition-all",
          searchFocused && "ring-1 ring-[#6D4AFF] rounded-lg"
        )}>
          <Search className="absolute left-3 w-4 h-4 text-gray-500" />
          <Input
            type="search"
            placeholder="Search messages..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            className={`w-full pl-10 pr-4 ${inputBg} ${inputBorder} ${inputText} placeholder:text-gray-500 focus-visible:ring-0 focus-visible:border-[#6D4AFF]`}
          />
        </div>
      </div>

      {/* Right Section */}
      <div className="flex items-center gap-1 sm:gap-2">
        {/* App Switcher - Hidden on mobile */}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className={`hidden sm:flex ${textColor} ${textHover} ${buttonHover}`}
            >
              <Grid3X3 className="w-5 h-5" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className={`w-64 ${popoverBg} ${borderColor} p-4`} align="end">
            <p className="text-xs text-gray-500 mb-3 uppercase tracking-wider">Apps</p>
            <div className="grid grid-cols-3 gap-3">
              {apps.map((app) => (
                <button
                  key={app.name}
                  disabled={app.disabled}
                  onClick={app.onClick}
                  className={cn(
                    "flex flex-col items-center gap-2 p-3 rounded-lg transition-colors",
                    app.active ? "bg-[#6D4AFF]/20 text-[#6D4AFF]" : `${buttonHover} ${textColor}`,
                    app.disabled && "opacity-50 cursor-not-allowed"
                  )}
                >
                  <span className="text-xl">{app.icon}</span>
                  <span className="text-xs">{app.name}</span>
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>

        {/* Messenger Toggle */}
        {onToggleMessenger && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggleMessenger}
            className={`relative ${textColor} ${textHover} ${buttonHover}`}
            title="Messenger"
          >
            <MessageCircle className="w-5 h-5" />
            {messengerUnreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                {messengerUnreadCount > 9 ? '9+' : messengerUnreadCount}
              </span>
            )}
          </Button>
        )}

        {/* Notifications Bell */}
        <Popover open={notificationsOpen} onOpenChange={setNotificationsOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className={`relative ${textColor} ${textHover} ${buttonHover}`}
              title="Notifications"
              data-testid="notifications-bell"
            >
              <Bell className="w-5 h-5" />
              {(friendRequests.length + broadcasts.filter(b => !b.is_read).length + supportNotifications.filter(n => !n.is_read).length) > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center animate-pulse">
                  {(friendRequests.length + broadcasts.filter(b => !b.is_read).length + supportNotifications.filter(n => !n.is_read).length) > 9 ? '9+' : (friendRequests.length + broadcasts.filter(b => !b.is_read).length + supportNotifications.filter(n => !n.is_read).length)}
                </span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent 
            className={`w-96 p-0 ${popoverBg} ${borderColor}`} 
            align="end"
          >
            <div className={`px-4 py-3 border-b ${borderColor} flex items-center justify-between`}>
              <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Notifications</h3>
              {broadcasts.filter(b => !b.is_read).length > 0 && (
                <button
                  onClick={handleMarkAllBroadcastsRead}
                  className="text-xs text-[#6D4AFF] hover:underline"
                >
                  Mark all as read
                </button>
              )}
            </div>
            <div className="max-h-96 overflow-y-auto">
              {(loadingRequests || loadingBroadcasts || loadingSupport) ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 text-[#6D4AFF] animate-spin" />
                </div>
              ) : (friendRequests.length === 0 && broadcasts.length === 0 && supportNotifications.length === 0) ? (
                <div className="py-8 text-center">
                  <Bell className={`w-10 h-10 mx-auto mb-2 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`} />
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No notifications</p>
                </div>
              ) : (
                <div className={`divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                  {/* Broadcasts Section */}
                  {broadcasts.length > 0 && (
                    <>
                      <div className={`px-3 py-2 ${isDarkMode ? 'bg-[#252540]' : 'bg-gray-50'}`}>
                        <p className={`text-xs font-medium uppercase tracking-wide ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          📢 Announcements
                        </p>
                      </div>
                      {broadcasts.slice(0, 5).map((broadcast) => (
                        <div 
                          key={broadcast.id} 
                          className={`p-3 ${isDarkMode ? 'hover:bg-[#252540]' : 'hover:bg-gray-50'} transition-colors cursor-pointer ${!broadcast.is_read ? (isDarkMode ? 'bg-[#6D4AFF]/10' : 'bg-purple-50') : ''}`}
                          onClick={() => handleMarkBroadcastRead(broadcast.id)}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                              broadcast.priority === 'urgent' ? 'bg-red-500' :
                              broadcast.priority === 'high' ? 'bg-orange-500' :
                              'bg-gradient-to-br from-[#6D4AFF] to-[#8B5CF6]'
                            }`}>
                              <Bell className="w-5 h-5 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'} ${!broadcast.is_read ? 'font-semibold' : ''}`}>
                                  {broadcast.title}
                                </p>
                                {!broadcast.is_read && (
                                  <span className="w-2 h-2 bg-[#6D4AFF] rounded-full"></span>
                                )}
                              </div>
                              <p className={`text-xs mt-1 line-clamp-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                {broadcast.message}
                              </p>
                              <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                {formatTimeAgo(broadcast.sent_at)}
                              </p>
                            </div>
                            {broadcast.is_read && (
                              <button
                                onClick={(e) => handleDismissBroadcast(broadcast.id, e)}
                                className={`flex-shrink-0 p-1 rounded-md transition-colors ${isDarkMode ? 'text-gray-600 hover:text-red-400 hover:bg-red-500/10' : 'text-gray-400 hover:text-red-500 hover:bg-red-50'}`}
                                title="Dismiss"
                              >
                                <XIcon className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                  
                  {/* Friend Requests Section */}
                  {friendRequests.length > 0 && (
                    <>
                      <div className={`px-3 py-2 ${isDarkMode ? 'bg-[#252540]' : 'bg-gray-50'}`}>
                        <p className={`text-xs font-medium uppercase tracking-wide ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          👥 Friend Requests
                        </p>
                      </div>
                      {friendRequests.map((request) => (
                        <div key={request.id} className={`p-3 ${isDarkMode ? 'hover:bg-[#252540]' : 'hover:bg-gray-50'} transition-colors`}>
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center flex-shrink-0">
                              <UserPlus className="w-5 h-5 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                <span className="font-medium">{request.from_name || request.from_email?.split('@')[0]}</span>
                                <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}> sent you a friend request</span>
                              </p>
                              <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                {request.from_email}
                              </p>
                              <div className="flex gap-2 mt-2">
                                <Button
                                  size="sm"
                                  onClick={() => handleRespondToRequest(request.id, 'accept')}
                                  style={{ backgroundColor: primaryColor }}
                                  className="h-7 hover:opacity-90 text-white"
                                  data-testid={`accept-request-${request.id}`}
                                >
                                  <Check className="w-3 h-3 mr-1" />
                                  Accept
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleRespondToRequest(request.id, 'decline')}
                                  className={`h-7 ${isDarkMode ? 'text-gray-400 hover:text-red-400' : 'text-gray-500 hover:text-red-500'}`}
                                  data-testid={`decline-request-${request.id}`}
                                >
                                  <XIcon className="w-3 h-3 mr-1" />
                                  Decline
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </>
                  )}

                  {/* Wall Subscriptions Section */}
                  {supportNotifications.filter(n => n.type === 'wall_post').length > 0 && (
                    <>
                      <div className={`px-3 py-2 ${isDarkMode ? 'bg-[#252540]' : 'bg-gray-50'}`}>
                        <p className={`text-xs font-medium uppercase tracking-wide ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          📰 Wall Subscriptions
                        </p>
                      </div>
                      {supportNotifications.filter(n => n.type === 'wall_post').slice(0, 5).map((notification) => (
                        <div 
                          key={notification.id} 
                          className={`p-3 ${isDarkMode ? 'hover:bg-[#252540]' : 'hover:bg-gray-50'} transition-colors cursor-pointer ${!notification.is_read ? (isDarkMode ? 'bg-green-500/10' : 'bg-green-50') : ''}`}
                          onClick={() => handleMarkSupportRead(notification.id)}
                        >
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center flex-shrink-0">
                              <Bell className="w-5 h-5 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'} ${!notification.is_read ? 'font-semibold' : ''}`}>
                                  {notification.title}
                                </p>
                                {!notification.is_read && (
                                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                                )}
                              </div>
                              <p className={`text-xs mt-1 line-clamp-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                {notification.message}
                              </p>
                              <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                {formatTimeAgo(notification.created_at)}
                              </p>
                            </div>
                            {notification.is_read && (
                              <button
                                onClick={(e) => handleDismissSupportNotification(notification.id, e)}
                                className={`flex-shrink-0 p-1 rounded-md transition-colors ${isDarkMode ? 'text-gray-600 hover:text-red-400 hover:bg-red-500/10' : 'text-gray-400 hover:text-red-500 hover:bg-red-50'}`}
                                title="Dismiss"
                                data-testid={`dismiss-wall-notification-${notification.id}`}
                              >
                                <XIcon className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </>
                  )}

                  {/* Support Responses Section */}
                  {supportNotifications.filter(n => n.type !== 'wall_post').length > 0 && (
                    <>
                      <div className={`px-3 py-2 ${isDarkMode ? 'bg-[#252540]' : 'bg-gray-50'}`}>
                        <p className={`text-xs font-medium uppercase tracking-wide ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          🎫 Support Responses
                        </p>
                      </div>
                      {supportNotifications.filter(n => n.type !== 'wall_post').slice(0, 5).map((notification) => (
                        <div 
                          key={notification.id} 
                          className={`p-3 ${isDarkMode ? 'hover:bg-[#252540]' : 'hover:bg-gray-50'} transition-colors cursor-pointer ${!notification.is_read ? (isDarkMode ? 'bg-blue-500/10' : 'bg-blue-50') : ''}`}
                          onClick={() => handleMarkSupportRead(notification.id)}
                        >
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center flex-shrink-0">
                              <Mail className="w-5 h-5 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'} ${!notification.is_read ? 'font-semibold' : ''}`}>
                                  {notification.title}
                                </p>
                                {!notification.is_read && (
                                  <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                                )}
                              </div>
                              <p className={`text-xs mt-1 line-clamp-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                {notification.message}
                              </p>
                              <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                {formatTimeAgo(notification.created_at)}
                              </p>
                            </div>
                            {notification.is_read && (
                              <button
                                onClick={(e) => handleDismissSupportNotification(notification.id, e)}
                                className={`flex-shrink-0 p-1 rounded-md transition-colors ${isDarkMode ? 'text-gray-600 hover:text-red-400 hover:bg-red-500/10' : 'text-gray-400 hover:text-red-500 hover:bg-red-50'}`}
                                title="Dismiss"
                                data-testid={`dismiss-support-notification-${notification.id}`}
                              >
                                <XIcon className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
          </PopoverContent>
        </Popover>

        {/* Notification Settings - Hidden on mobile */}
        <div className="hidden sm:block">
          <NotificationSettings isDarkMode={isDarkMode} />
        </div>

        {/* Contact Button - Hidden on mobile */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setContactModalOpen(true)}
                className={`hidden sm:flex ${textColor} ${textHover} ${buttonHover}`}
                data-testid="contact-us-btn"
              >
                <Phone className="w-5 h-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent className={`${popoverBg} ${borderColor}`}>
              <p className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>Contact Us</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Theme Toggle - Hidden on mobile (accessible via settings) */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggleTheme || toggleTheme}
          className={`hidden sm:flex ${textColor} ${textHover} ${buttonHover}`}
          title={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
        >
          {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </Button>

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="relative h-9 w-9 rounded-full">
              <Avatar className="h-9 w-9">
                <AvatarFallback className="bg-gradient-to-br from-[#6D4AFF] to-[#8B5CF6] text-white text-sm">
                  {user?.avatar || 'U'}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className={`w-64 ${popoverBg} ${borderColor}`} align="end">
            <DropdownMenuLabel className="font-normal">
              <div className="flex items-center gap-3">
                <Avatar className="h-10 w-10">
                  <AvatarFallback className="bg-gradient-to-br from-[#6D4AFF] to-[#8B5CF6] text-white">
                    {user?.avatar || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{user?.name || 'User'}</p>
                  <p className="text-xs text-gray-500">{user?.email || ''}</p>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator className={isDarkMode ? 'bg-[#2d2d44]' : 'bg-gray-200'} />
            <div className="px-2 py-2">
              <Badge className="bg-[#6D4AFF]/20 text-[#6D4AFF] hover:bg-[#6D4AFF]/30">
                {user?.plan || 'User'}
              </Badge>
            </div>
            <DropdownMenuSeparator className={isDarkMode ? 'bg-[#2d2d44]' : 'bg-gray-200'} />
            {isAdmin && (
              <>
                <DropdownMenuItem 
                  className={`${isDarkMode ? 'text-gray-300 hover:text-white focus:bg-[#2d2d44]' : 'text-gray-700 hover:text-gray-900 focus:bg-gray-100'} cursor-pointer`}
                  onClick={() => navigate('/admin')}
                >
                  <Users className="w-4 h-4 mr-2" />
                  Admin Dashboard
                </DropdownMenuItem>
                <DropdownMenuItem 
                  className={`${isDarkMode ? 'text-gray-300 hover:text-white focus:bg-[#2d2d44]' : 'text-gray-700 hover:text-gray-900 focus:bg-gray-100'} cursor-pointer`}
                  onClick={() => navigate('/developer')}
                  data-testid="developer-settings-menu"
                >
                  <Code className="w-4 h-4 mr-2" />
                  Developer Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator className={isDarkMode ? 'bg-[#2d2d44]' : 'bg-gray-200'} />
              </>
            )}
            {(isAdmin || user?.role === 'support') && (
              <DropdownMenuItem 
                className={`${isDarkMode ? 'text-gray-300 hover:text-white focus:bg-[#2d2d44]' : 'text-gray-700 hover:text-gray-900 focus:bg-gray-100'} cursor-pointer`}
                onClick={() => navigate('/support-dashboard')}
              >
                <Shield className="w-4 h-4 mr-2 text-emerald-400" />
                Support Dashboard
              </DropdownMenuItem>
            )}
            <DropdownMenuItem 
              className={`${isDarkMode ? 'text-gray-300 hover:text-white focus:bg-[#2d2d44]' : 'text-gray-700 hover:text-gray-900 focus:bg-gray-100'} cursor-pointer`}
              onClick={() => navigate('/settings')}
            >
              <User className="w-4 h-4 mr-2" />
              Account settings
            </DropdownMenuItem>
            <DropdownMenuItem 
              className={`${isDarkMode ? 'text-gray-300 hover:text-white focus:bg-[#2d2d44]' : 'text-gray-700 hover:text-gray-900 focus:bg-gray-100'} cursor-pointer`}
              onClick={() => navigate('/settings')}
            >
              <Shield className="w-4 h-4 mr-2" />
              Security & privacy
            </DropdownMenuItem>
            <DropdownMenuItem 
              className="text-orange-400 hover:text-orange-300 focus:bg-[#2d2d44] cursor-pointer"
              onClick={() => setReportModalOpen(true)}
              data-testid="report-issue-btn"
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              Report an Issue
            </DropdownMenuItem>
            <DropdownMenuSeparator className={isDarkMode ? 'bg-[#2d2d44]' : 'bg-gray-200'} />
            
            {/* Invite Friends Button */}
            <div className="px-2 py-2">
              <Button
                onClick={() => navigate('/referrals')}
                data-testid="invite-friends-btn"
                className="w-full bg-gradient-to-r from-[#6D4AFF] to-[#8B5CF6] hover:from-[#5B3FD9] hover:to-[#7C4FE0] text-white"
              >
                <Gift className="w-4 h-4 mr-2" />
                Invite Friends
              </Button>
            </div>
            
            <DropdownMenuSeparator className={isDarkMode ? 'bg-[#2d2d44]' : 'bg-gray-200'} />
            <DropdownMenuItem 
              className="text-red-400 hover:text-red-300 focus:bg-[#2d2d44] cursor-pointer"
              onClick={async () => await onLogout()}
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      
      {/* Report Modal */}
      <ReportModal
        open={reportModalOpen}
        onClose={() => setReportModalOpen(false)}
      />
      
      {/* Contact Form Modal */}
      <ContactFormModal
        open={contactModalOpen}
        onClose={() => setContactModalOpen(false)}
      />
    </header>
  );
}
